import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import {
  completeChecklistStep,
  getCatalog,
  getFullProductTemplateInfo,
  uncompleteChecklistStep,
} from 'api/operations/productServiceV2';
import { productServiceHelper } from 'global/services-helpers';
import { makeAutoObservable, runInAction } from 'mobx';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import {
  CompletionStatus,
  EngValue,
  MaxWaiver,
  PlanAttributeCategory,
  PlanAttributeType,
  ProductTemplate,
  ProductTemplateChecklistCard,
  ProductTemplateStatus,
} from 'types/productServiceV2';

export interface AttributeInfo {
  id: string;
  name: string;
  formValues?: Record<string, string>;
  engValues?: EngValue[];
  isCovered: boolean;
  type: PlanAttributeType;
  maxWaiverMessage?: MaxWaiver;
  attributeCategory: PlanAttributeCategory;
}

interface ShortPlanAttribute {
  name: string;
  id: string;
}

export default class UWProductsModule {
  rootStore: RootStore;

  private _productTemplate: ProductTemplate | null = null;
  private _requiredAttributes: AttributeInfo[] = [];
  private _customAttributes: AttributeInfo[] = [];
  private _catalogId: string = '';

  // Getters
  public get productTemplate(): ProductTemplate | null {
    return this._productTemplate;
  }

  /** List of all attribute IDs for progress status calculation and easy access to attribute form(via Next/Back buttons) */
  public get planAttributeIDs(): string[] {
    return [...this.requiredAttributes, ...this.customAttributes].map((attribute) => attribute.id);
  }

  public get requiredAttributes(): AttributeInfo[] {
    return this._requiredAttributes;
  }

  public get customAttributes(): AttributeInfo[] {
    return this._customAttributes;
  }

  public get benefitSummaryLink(): string {
    return this._productTemplate?.planSummary || '';
  }

  public get catalogId(): string {
    return this._catalogId;
  }

  // Setters
  public setProductTemplate(template: ProductTemplate | null): void {
    this._productTemplate = template;
  }

  private setRequiredAttributes(attributesInfo: AttributeInfo[]): void {
    this._requiredAttributes = attributesInfo;
  }

  private setCustomAttributes(attributesInfo: AttributeInfo[]): void {
    this._customAttributes = attributesInfo;
  }

  private setCatalogId(catalogId: string): void {
    this._catalogId = catalogId;
  }

  // Api calls
  private async getCatalog(effectiveDate: string, productLineId: ProductLineId): Promise<void> {
    /**
     * const rateSegment = this.productTemplate?.rateSegment || 'CUSTOM';
     * Hardcoding 'CUSTOM' as in current implementation list of required attributes always in the CUSTOM Catalog
     * For example, Altus case - even if we populate Shelf Product, "under the hood" attributes should be from CUSTOM catalog
     */
    const rateSegment = 'CUSTOM';

    const res = await getCatalog(this.rootStore.userStore.sponsorId, effectiveDate, rateSegment);
    runInAction(() => {
      const requiredAttributes = productServiceHelper.getProductLineAttributesFromCatalog(
        res.data.getCatalog,
        productLineId,
        rateSegment,
      );
      this.setCatalogId(res.data.getCatalog.id);
      const planAttributeNamesAndIDs = requiredAttributes.map(({ id, displayName }) => ({
        id,
        name: displayName,
      }));
      const requiredAttributesWithAnswers = this.combineFullAttributeInfo(
        planAttributeNamesAndIDs,
        PlanAttributeCategory.REQUIRED,
      );

      this.setRequiredAttributes(requiredAttributesWithAnswers);
    });
  }

  public async getProductTemplate(templateId: string): Promise<void> {
    const res = await getFullProductTemplateInfo(templateId);
    runInAction(() => {
      if (res) {
        this.setProductTemplate(res);
        // Custom attributes aren’t part of the catalog — they come from template form answers
        this.getCustomAttributeInfo();
      }
    });
  }

  public async getInitialData(templateId: string): Promise<void> {
    await this.getProductTemplate(templateId);
    await runInAction(async () => {
      if (this.productTemplate) {
        await this.getCatalog(this.productTemplate.effectiveFrom, this.productTemplate.productLineId);
      }
    });
  }

  public async onCompleteStep(step: ProductTemplateChecklistCard): Promise<void> {
    if (!this.productTemplate) return;
    await completeChecklistStep(this.productTemplate.id, step);
  }

  public async onUncompleteStep(step: ProductTemplateChecklistCard): Promise<void> {
    if (!this.productTemplate) return;
    const template = await uncompleteChecklistStep(this.productTemplate.id, step);
    runInAction(() => {
      this.setProductTemplate(template);
    });
  }

  // Other
  public isStepCompleted(card: ProductTemplateChecklistCard): boolean {
    return (
      this.productTemplate?.checklist.checklistOptions.find(
        (option) => option.card === ProductTemplateChecklistCard[card],
      )?.completionStatus === CompletionStatus.COMPLETED
    );
  }

  /**
   * A template with the status `IN_PROGRESS` or `INITIAL_SEED`.
   * These statuses indicate that the template is still editable, allowing changes to be made
   * until it is `READY`.
   */
  public get isTemplateInProgress(): boolean {
    return (
      this.productTemplate?.status === ProductTemplateStatus.IN_PROGRESS ||
      this.productTemplate?.status === ProductTemplateStatus.INITIAL_SEED
    );
  }

  private combineFullAttributeInfo(
    planAttributesInfo: ShortPlanAttribute[],
    attributeCategory: PlanAttributeCategory,
  ): AttributeInfo[] {
    const fullAttributesInfoList = planAttributesInfo.map((shortAttributeInfo) => {
      return this.getAttributeInfo(shortAttributeInfo, attributeCategory);
    });
    return fullAttributesInfoList;
  }

  private getAttributeInfo({ id, name }: ShortPlanAttribute, attributeCategory: PlanAttributeCategory): AttributeInfo {
    // The key in the formAnswers object
    const formAttributes = attributeCategory === PlanAttributeCategory.REQUIRED ? 'planAttributes' : 'customAttributes';
    // General
    const templateEngValues = this.productTemplate?.formAnswers.engValues;
    const attributeAnswers = this.productTemplate?.formAnswers[formAttributes];

    // Attribute specific information
    const engValues = templateEngValues && templateEngValues[id];
    const formValues = attributeAnswers && attributeAnswers[id];
    const isCovered = (engValues && engValues?.length > 0) || false;
    return {
      id,
      name,
      formValues,
      engValues,
      isCovered,
      type: productServiceHelper.getPlanAttributeStatus(engValues),
      maxWaiverMessage: this.productTemplate?.maxWaiverMessage,
      attributeCategory,
    };
  }

  private getCustomAttributeInfo(): void {
    const customAttributes: ShortPlanAttribute[] = Object.keys(
      this.productTemplate?.formAnswers.customAttributes || {},
    ).map((attribute) => ({
      id: attribute,
      name: attribute,
    }));
    const customAttributesWithAnswers = this.combineFullAttributeInfo(customAttributes, PlanAttributeCategory.CUSTOM);
    this.setCustomAttributes(customAttributesWithAnswers);
  }

  public getPlanAttributeNameById(attributeId: string): string {
    return (
      [...this.requiredAttributes, ...this.customAttributes].find((attribute) => attribute.id === attributeId)?.name ||
      ''
    );
  }

  public getPlanAttributeCategoryById(attributeId: string): PlanAttributeCategory {
    return this.requiredAttributes.some((attr) => attr.id === attributeId)
      ? PlanAttributeCategory.REQUIRED
      : PlanAttributeCategory.CUSTOM;
  }

  public get attributesProgressStatus(): string {
    const allAttributes = {
      ...this.productTemplate?.formAnswers.planAttributes,
      ...this.productTemplate?.formAnswers.customAttributes,
    };

    const totalCountOfFilledAttributes = allAttributes
      ? this.planAttributeIDs.reduce((count, domainAttribute) => count + (allAttributes[domainAttribute] ? 1 : 0), 0)
      : 0;

    return `${totalCountOfFilledAttributes}/${this.planAttributeIDs.length} attributes are completed`;
  }

  public resetInitialData(): void {
    this.setProductTemplate(null);
    this.setRequiredAttributes([]);
    this.setCustomAttributes([]);
  }

  constructor() {
    this.rootStore = getRootStoreInstance();
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
